// 미리 채우기 미리보기를 PNG 이미지로 내보내기.
//
// 운영자 요청 — 발송 전에 "채워진 상태"를 파일로 남겨 사내 공유·검토에 쓴다.
// 화면 캡처가 아니라 PDF 를 고배율로 다시 렌더하고 그 위에 값을 그린다.
// 화면 줌과 무관하게 항상 같은 해상도가 나오고, 에디터 크롬(점선 테두리,
// '자동:' 칩, 스크롤바)이 섞이지 않는다.
//
// 담는 내용 = 직접 입력(literal 바인딩) 필드의 값만.
// 자동 채움·서명·도장은 발송 시점에 서버/고객이 채우므로 이 단계에선 값이
// 없다 → 빈칸으로 남긴다. 호출부에서 이 점을 UI 로 알린다.

import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { signatureFieldFontPx, type SignatureField } from '@bhops/shared';
// 합류(26-08-05) — '오늘'은 KST 정본으로만 만든다(아래 resolvePrefillDisplayValue 주석).
import { kstTodayYmd } from '@/lib/format-date';

if (typeof window !== 'undefined' && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
}

/** 좌표계 — 디자이너/미리보기와 동일 (A4 @ 96dpi). */
const PAGE_W = 794;
const PAGE_H = 1122;

/**
 * 내보내기 배율. A4 794px × 3 ≈ 2382 × 3366px (≈288dpi) — 인쇄·확대 검토에
 * 충분하다. 화면 줌과 완전히 무관하게 고정이라 언제 눌러도 같은 품질이 나온다.
 */
const EXPORT_SCALE = 3;

/** Canvas 2D 는 CSS 변수를 못 읽는다. 문서 잉크색은 테마와 무관하게 고정. */
const INK = '#1a1a2e';

/** 한글이 깨지지 않도록 Windows 기본 한글 폰트를 우선 지정. */
const FONT_STACK = '"Malgun Gothic", "맑은 고딕", system-ui, sans-serif';

/**
 * 미리보기와 **동일한** 표시값 결정 규칙.
 *
 * 서버가 발송 시 빈 값에 넣는 기본값을 화면에서 미리 반영하는 것까지 포함한다:
 *   빈 date → 오늘(YYYY-MM-DD), 빈 text/dropdown → defaultValue.
 *
 * 미리보기 렌더와 내보내기가 갈리지 않도록 양쪽이 이 함수를 공유한다.
 *
 * ⚠ '오늘'은 **KST 기준**이다(`kstTodayYmd`). 합류(26-08-05) 전 이 함수는
 *   `new Date().toISOString().slice(0,10)` 이었는데 그것은 **UTC** 라, 한국 시간
 *   오전 9시 전에는 **어제 날짜**를 내놓는다(추가수정 2차 26-07-27 §2 에서 화면 쪽을
 *   이미 고쳤던 버그가 이 함수로 되돌아온 것). 화면과 내보내기가 이 함수를 공유하므로
 *   여기 한 곳을 고치면 저장한 PNG 의 날짜까지 함께 맞는다 — 그 공유가 이 함수의 목적이다.
 *   지역 구현(+9h 시프트)을 새로 쓰지 말 것: KST 포맷터 정본은 `@/lib/format-date` 한 벌이다.
 */
export function resolvePrefillDisplayValue(field: SignatureField, raw: string): string {
  const value = raw ?? '';
  if (value !== '') return value;
  if (field.type === 'date') return kstTodayYmd();
  return field.defaultValue ?? '';
}

/** 미리보기와 동일한 글자 크기 규칙. */
function fontSizeFor(field: SignatureField): number {
  return signatureFieldFontPx(field, Math.min(12, Math.max(field.h, 12) * 0.5));
}

/** 파일명에 못 쓰는 문자 제거. */
function safeFilename(name: string): string {
  return (name || 'document')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, ' ')
    .trim();
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // revoke 를 즉시 하면 일부 브라우저가 다운로드를 취소한다. 한 tick 뒤에.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('이미지 인코딩에 실패했습니다.'));
    }, 'image/png');
  });
}

/** 체크박스 — 미리보기와 같은 모양(사각 테두리 + 체크). */
function drawCheckbox(
  ctx: CanvasRenderingContext2D,
  field: SignatureField,
  localY: number,
  checked: boolean,
): void {
  const side = Math.min(field.w, field.h) * 0.7 * EXPORT_SCALE;
  const cx = (field.x + field.w / 2) * EXPORT_SCALE;
  const cy = (localY + field.h / 2) * EXPORT_SCALE;
  const left = cx - side / 2;
  const top = cy - side / 2;

  ctx.strokeStyle = INK;
  ctx.lineWidth = Math.max(1, EXPORT_SCALE * 0.8);
  ctx.strokeRect(left, top, side, side);

  if (!checked) return;
  ctx.beginPath();
  ctx.lineWidth = Math.max(1.5, EXPORT_SCALE * 1.2);
  ctx.lineCap = 'round';
  ctx.moveTo(left + side * 0.22, top + side * 0.55);
  ctx.lineTo(left + side * 0.43, top + side * 0.75);
  ctx.lineTo(left + side * 0.8, top + side * 0.25);
  ctx.stroke();
}

/** 텍스트성 필드 — 정렬/클리핑을 미리보기와 맞춘다. */
function drawTextField(
  ctx: CanvasRenderingContext2D,
  field: SignatureField,
  localY: number,
  text: string,
): void {
  if (text === '') return;

  const pad = 4 * EXPORT_SCALE;
  const boxX = field.x * EXPORT_SCALE;
  const boxY = localY * EXPORT_SCALE;
  const boxW = field.w * EXPORT_SCALE;
  const boxH = field.h * EXPORT_SCALE;

  ctx.save();
  // overflow: hidden 과 동일하게 박스 밖으로 삐져나가지 않게.
  ctx.beginPath();
  ctx.rect(boxX, boxY, boxW, boxH);
  ctx.clip();

  ctx.fillStyle = INK;
  ctx.font = `${fontSizeFor(field) * EXPORT_SCALE}px ${FONT_STACK}`;
  ctx.textBaseline = 'middle';

  const cy = boxY + boxH / 2;
  if (field.align === 'center') {
    ctx.textAlign = 'center';
    ctx.fillText(text, boxX + boxW / 2, cy);
  } else if (field.align === 'right') {
    ctx.textAlign = 'right';
    ctx.fillText(text, boxX + boxW - pad, cy);
  } else {
    ctx.textAlign = 'left';
    ctx.fillText(text, boxX + pad, cy);
  }
  ctx.restore();
}

/**
 * 내보내기 전체 제한 시간. 초과하면 실패로 알린다.
 *
 * pdf.js 의 page.render() 는 내부적으로 requestAnimationFrame 을 쓴다. 탭이
 * hidden 이면 rAF 가 발화하지 않아 렌더가 **영구히 멈춘다** — 그대로 두면
 * 버튼이 '저장 중…' 에 갇혀 사용자가 원인을 알 수 없다. 사용자가 직접 누르는
 * 동작이라 보통은 탭이 보이지만(=정상 진행), 도중에 탭을 백그라운드로 보내면
 * 실제로 발생한다.
 */
const EXPORT_TIMEOUT_MS = 60_000;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () =>
        reject(
          new Error(
            '시간이 초과되었습니다. 이 탭을 화면에 띄워 둔 채로 다시 시도해 주세요. ' +
              '브라우저는 뒤에 가려진 탭의 화면을 그리지 않습니다.',
          ),
        ),
      ms,
    );
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

export interface ExportPrefillOptions {
  pdfUrl: string;
  fields: SignatureField[];
  values: Record<string, string>;
  /** 문서의 페이지 수. PDF 실제 페이지 수와 다르면 PDF 쪽을 따른다. */
  pageCount: number;
  /** 파일명 앞부분 (문서명 등). */
  filenameBase: string;
  /**
   * 이 페이지만(1부터). 없으면 전부. 2026-10-20 오너 — '페리페럴 리스트 받기'는 2페이지만 받는다.
   * 문서에 없는 페이지는 건너뛴다. 한 장만 받으면 파일명에 `_pN` 을 붙이지 않는다.
   */
  pages?: number[];
}

/**
 * 각 페이지를 PNG 로 내보낸다. 여러 페이지면 파일이 페이지당 하나씩 생긴다
 * (한 장으로 합치면 50페이지 문서에서 canvas 최대 높이를 넘긴다).
 *
 * @returns 저장한 페이지 수
 */
export async function exportPrefillImages(opts: ExportPrefillOptions): Promise<number> {
  return withTimeout(runExport(opts), EXPORT_TIMEOUT_MS);
}

async function runExport(opts: ExportPrefillOptions): Promise<number> {
  const { pdfUrl, fields, values, filenameBase } = opts;

  const pdf = await pdfjsLib.getDocument({ url: pdfUrl }).promise;
  try {
    const total = pdf.numPages;
    const base = safeFilename(filenameBase);
    const wanted = opts.pages
      ? opts.pages.filter((n) => Number.isInteger(n) && n >= 1 && n <= total)
      : Array.from({ length: total }, (_, i) => i + 1);
    if (wanted.length === 0) throw new Error('받을 페이지가 문서에 없습니다.');

    for (const [k, pageNo] of wanted.entries()) {
      const page = await pdf.getPage(pageNo);
      const baseViewport = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({
        scale: (PAGE_W / baseViewport.width) * EXPORT_SCALE,
      });

      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      // 장 높이를 PAGE_H 에 정확히 맞춘다(화면 뷰어와 같은 규칙 — 입력 값이 표 선과 어긋나지 않게).
      canvas.height = Math.floor(PAGE_H * EXPORT_SCALE);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('이미지를 그릴 수 없습니다. 다른 브라우저에서 다시 시도해 주세요.');

      // PNG 는 투명 배경이라 종이 흰색을 먼저 깔아야 한다.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.save();
      ctx.scale(1, canvas.height / viewport.height);
      await page.render({ canvasContext: ctx, viewport }).promise;
      ctx.restore();

      // 이 페이지에 속한 직접 입력 필드만 그린다.
      const pageIdx = pageNo - 1;
      for (const field of fields) {
        if (field.pageIndex !== pageIdx) continue;
        if (field.binding.kind !== 'literal') continue;
        if (field.type === 'signature' || field.type === 'stamp' || field.type === 'image')
          continue;

        const localY = field.y - pageIdx * PAGE_H;
        const raw = values[field.id] ?? '';

        if (field.type === 'checkbox') {
          drawCheckbox(ctx, field, localY, raw === 'true');
        } else {
          drawTextField(ctx, field, localY, resolvePrefillDisplayValue(field, raw));
        }
      }

      const blob = await canvasToBlob(canvas);
      const suffix = wanted.length > 1 ? `_p${pageNo}` : '';
      triggerDownload(blob, `${base}${suffix}.png`);

      // 연속 다운로드를 브라우저가 묶어서 막지 않도록 약간 간격을 둔다.
      if (k < wanted.length - 1) await new Promise((r) => setTimeout(r, 300));
    }

    return wanted.length;
  } finally {
    void pdf.destroy();
  }
}
